import { NgModule } from '@angular/core';
import { RouterModule, Routes, UrlMatchResult, UrlSegment } from '@angular/router';

import { BusinessGuard } from './guards/business.guard';
import { ProfileGuard } from './guards/profile.guard';
import { Constants } from './services/constants';
import { NotificationsPageComponent } from './shared/components/notifications-page/notifications-page';
import { ThankYouCommonComponent } from './shared/components/thank-you-common/thank-you-common';

function tenantTermsMatcher(termsPath: string) {
  return (segments: UrlSegment[]): UrlMatchResult | null => {
    if (segments.length !== 2 || segments[1].path !== termsPath) {
      return null;
    }

    const tenant = segments[0];
    if (!Constants.WORKCOVER_PLATFORMS.includes(tenant.path.toLowerCase())) {
      return null;
    }

    return { consumed: segments, posParams: { tenant } };
  };
}

const routes: Routes = [
  {
    // Website pages, including the homepage at / and the legal pages (privacy policy, terms etc.). Kept last so existing routes match first.
    path: '',
    loadChildren: () => import('./modules/website/website.routes').then((mod) => mod.WEBSITE_PAGE_ROUTES)
  },
  {
    path: 'success-stories',
    loadChildren: () =>
      import('./modules/success-stories/success-stories.routes').then((mod) => mod.SUCCESS_STORIES_ROUTES)
  },
  {
    path: 'notfound',
    loadComponent: () => import('./shared/components/not-found/not-found').then((mod) => mod.NotFoundComponent)
  },
  {
    path: 'login',
    loadComponent: () => import('./modules/auth-check/sign-in/sign-in').then((mod) => mod.SignInComponent)
  },
  {
    path: 'sign-up',
    redirectTo: '/business/provider/signup'
  },
  {
    path: 'maintenance',
    loadComponent: () => import('./modules/maintenance/maintenance').then((mod) => mod.MaintenanceComponent)
  },
  {
    path: 'business/provider/signup',
    loadComponent: () =>
      import('./modules/auth-check/business-sign-up/business-sign-up').then((mod) => mod.BusinessSignUpComponent)
  },
  {
    path: 'work-cover-signup/internal',
    loadChildren: () =>
      import('./modules/workcover-business-sign-up/workcover-business-sign-up.routes').then(
        (mod) => mod.WORKCOVER_BUSINESS_SIGN_UP_ROUTES
      )
  },
  {
    path: 'work-cover/sign-up',
    loadChildren: () =>
      import('./modules/workcover-business-sign-up/workcover-business-sign-up.routes').then(
        (mod) => mod.WORKCOVER_BUSINESS_SIGN_UP_ROUTES
      )
  },
  {
    path: 'forgot-password',
    loadComponent: () =>
      import('./modules/auth-check/forgot-password/forgot-password').then((mod) => mod.ForgotPasswordComponent)
  },
  {
    path: 'create-password/:slug/:token',
    data: {
      password: 'customer'
    },
    loadComponent: () =>
      import('./modules/auth-check/create-password/create-password').then((mod) => mod.CreatePasswordComponent)
  },
  {
    path: 'create-business-password/:slug/:token',
    data: {
      password: 'business'
    },
    loadComponent: () =>
      import('./modules/auth-check/create-password/create-password').then((mod) => mod.CreatePasswordComponent)
  },
  {
    path: 'create-practitioner-password/:slug/:token',
    data: {
      password: 'practitioner'
    },
    loadComponent: () =>
      import('./modules/auth-check/create-password/create-password').then((mod) => mod.CreatePasswordComponent)
  },
  {
    path: 'reset-password/:slug/:token',
    loadComponent: () =>
      import('./modules/auth-check/activate-account/activate-account').then((mod) => mod.ActivateAccountComponent)
  },
  {
    path: 'workplacewellbeing',
    loadComponent: () =>
      import('./modules/workplace-wellbeing-site/workplace-main/workplace-main').then(
        (mod) => mod.WorkplaceMainComponent
      )
  },
  {
    path: 'profile',
    loadChildren: () => import('./modules/profile-new/profile-new.routes').then((mod) => mod.PROFILE_NEW_ROUTES),
    canActivate: [ProfileGuard]
  },
  {
    path: 'business',
    loadChildren: () =>
      import('./modules/business-dashboard/business-dashboard.routes').then((mod) => mod.BUSINESS_DASHBOARD_ROUTES),
    canActivate: [BusinessGuard]
  },
  {
    // Old about page, replaced by the new website's /about
    path: 'about-avaana',
    redirectTo: '/about'
  },
  {
    path: 'sitemap',
    loadComponent: () => import('./modules/terms/sitemap/sitemap').then((mod) => mod.SiteMapComponent)
  },
  {
    path: 'provider-terms',
    loadComponent: () =>
      import('./modules/terms/provider-terms/provider-terms').then((mod) => mod.ProviderTermsComponent)
  },
  {
    path: 'community-guidelines',
    loadComponent: () =>
      import('./modules/terms/community-guidelines/community-guidelines').then(
        (mod) => mod.CommunityGuidelinesComponent
      )
  },
  {
    matcher: tenantTermsMatcher('privacy-policy'),
    data: { termsType: 'privacy-policy' },
    loadComponent: () => import('./modules/tenant-terms/tenant-terms').then((mod) => mod.TenantTermsComponent)
  },
  {
    matcher: tenantTermsMatcher('booking-terms'),
    data: { termsType: 'booking-terms' },
    loadComponent: () => import('./modules/tenant-terms/tenant-terms').then((mod) => mod.TenantTermsComponent)
  },
  {
    path: 'notifications',
    component: NotificationsPageComponent
  },
  {
    path: 'thank-you',
    component: ThankYouCommonComponent
  },
  {
    // Render in place (no redirect) so the unknown URL itself returns a 404 status
    path: '**',
    loadComponent: () => import('./shared/components/not-found/not-found').then((mod) => mod.NotFoundComponent)
  }
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      initialNavigation: 'enabledBlocking'
    })
  ],
  exports: [RouterModule]
})
export class AppRoutingModule {}
